import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { AccountabilityChartDialog } from 'pages/Tenant/AROKMS/KTree/components/AccountabilityChartDialog';
import { useGetMyAccountabilities } from 'services/v1/Tenant/AccountabilityService';
import { MyAccountabilityResponse } from 'types/api/Tenant/AccountabilityTypes';

export interface AccountabilityDisplayItem {
  id: string;
  name: string;
  type: string;
  manager: string;
  responsibility: string;
  managedByCurrentUser: boolean;
}

interface DisplayAccouuntabilityProps {
  accountabilityDefinitionId: string;
  onItemSelect?: (item: AccountabilityDisplayItem) => void;
}

const TABLE_MIN_WIDTH = 900;
const BORDER_COLOR = '#E2E7EF';
const TEXT_COLOR = '#344054';
const SECONDARY_TEXT_COLOR = '#667085';
const LINK_COLOR = '#3F50B5';

export function mapMyAccountabilitiesToDisplayItems(
  response?: MyAccountabilityResponse
): AccountabilityDisplayItem[] {
  if (!response?.levels?.length) return [];

  return response.levels.flatMap((level) =>
    (level.items ?? [])
      .filter((item) => Boolean(item.member?.trim()))
      .map((item) => {
        const name = item.member?.trim() ?? 'Unnamed organisational unit';
        const managedByCurrentUser = item.accountabilityType === 'DIRECT';

        return {
          id: item.ktreeMemberId ?? item.sourceRecordId ?? `${level.level}-${name}`,
          name,
          type: level.levelName?.trim() || `Level ${level.level}`,
          manager: managedByCurrentUser ? 'You' : item.accountablePersonName?.trim() || 'Unassigned',
          responsibility: `${name} manager`,
          managedByCurrentUser,
        };
      })
  );
}

type AccountabilityGroupProps = {
  color: string;
  backgroundColor: string;
  label: string;
  items: AccountabilityDisplayItem[];
  selectedItemId: string | null;
  onSelect: (item: AccountabilityDisplayItem) => void;
};

function AccountabilityGroup({
  color,
  backgroundColor,
  label,
  items,
  selectedItemId,
  onSelect,
}: Readonly<AccountabilityGroupProps>) {
  if (items.length === 0) return null;

  return (
    <>
      <TableRow>
        <TableCell
          colSpan={5}
          sx={{
            position: 'relative',
            height: 38,
            px: 2.1,
            py: 0.8,
            borderBottom: 0,
            color,
            backgroundColor,
          
          }}
        >
          <Stack direction='row' alignItems='center' justifyContent='space-between' gap={2}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, lineHeight: 1.3, textTransform: 'uppercase' }}>
              {label}
            </Typography>
            <Typography sx={{ pr: 2.4, fontSize: 12, fontWeight: 800, lineHeight: 1.3 }}>
              {items.length}
            </Typography>
          </Stack>
        </TableCell>
      </TableRow>

      {items.map((item) => {
        const isSelected = selectedItemId === item.id;

        return (
          <TableRow
            key={item.id}
            data-testid={`accountability-row-${item.id}`}
            aria-selected={isSelected}
            sx={{
              backgroundColor: isSelected ? 'rgba(63, 80, 181, 0.055)' : '#FFFFFF',
              transition: 'background-color 150ms ease',
              '&:hover': { backgroundColor: isSelected ? 'rgba(63, 80, 181, 0.08)' : '#FAFBFC' },
              '&:last-of-type td': { borderBottomColor: BORDER_COLOR },
            }}
          >
            <TableCell sx={{ width: '37%', px: 2.1, py: 1.35, borderBottomColor: BORDER_COLOR }}>
              <ButtonBase
                type='button'
                onClick={() => onSelect(item)}
                sx={{
                  borderRadius: 0.5,
                  color: LINK_COLOR,
                  fontSize: 15,
                  fontWeight: 800,
                  lineHeight: 1.35,
                  textAlign: 'left',
                  '&:hover': { textDecoration: 'underline', textUnderlineOffset: '3px' },
                  '&:focus-visible': {
                    outline: '3px solid rgba(63, 80, 181, 0.2)',
                    outlineOffset: 2,
                  },
                }}
              >
                {item.name}
              </ButtonBase>
            </TableCell>
            <TableCell sx={{ width: '17%', px: 2.1, py: 1.35, borderBottomColor: BORDER_COLOR }}>
              <Typography sx={{ color: SECONDARY_TEXT_COLOR, fontSize: 12, fontWeight: 600, lineHeight: 1.4 }}>
                {item.type}
              </Typography>
            </TableCell>
            <TableCell sx={{ width: '14%', px: 2.1, py: 1.35, borderBottomColor: BORDER_COLOR }}>
              {item.managedByCurrentUser ? (
                <Box
                  component='span'
                  sx={{
                    display: 'inline-flex',
                    minWidth: 50,
                    minHeight: 25,
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 1.2,
                    borderRadius: 99,
                    color: '#168F70',
                    backgroundColor: '#DDF7EF',
                    fontSize: 12,
                    fontWeight: 800,
                    lineHeight: 1,
                  }}
                >
                  You
                </Box>
              ) : (
                <Typography sx={{ color: TEXT_COLOR, fontSize: 12, fontWeight: 800, lineHeight: 1.4 }}>
                  {item.manager}
                </Typography>
              )}
            </TableCell>
            <TableCell sx={{ width: '27%', px: 2.1, py: 1.35, borderBottomColor: BORDER_COLOR }}>
              <Typography sx={{ color: TEXT_COLOR, fontSize: 12, fontWeight: 600, lineHeight: 1.4 }}>
                {item.responsibility}
              </Typography>
            </TableCell>
            <TableCell align='right' sx={{ width: '5%', px: 1.5, py: 0.75, borderBottomColor: BORDER_COLOR }}>
           
            </TableCell>
          </TableRow>
        );
      })}
    </>
  );
}

export function DisplayAccouuntability({
  accountabilityDefinitionId,
  onItemSelect,
}: Readonly<DisplayAccouuntabilityProps>) {
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [isChartOpen, setIsChartOpen] = useState(false);
  const { data, isLoading, isError, refetch } = useGetMyAccountabilities(accountabilityDefinitionId);
  const items = useMemo(() => mapMyAccountabilitiesToDisplayItems(data?.data), [data?.data]);
  const managedByCurrentUser = useMemo(() => items.filter((item) => item.managedByCurrentUser), [items]);
  const managedByColleagues = useMemo(() => items.filter((item) => !item.managedByCurrentUser), [items]);
  const selectedItem = useMemo(() => items.find((item) => item.id === selectedItemId) ?? null, [items, selectedItemId]);

  const handleSelect = (item: AccountabilityDisplayItem) => {
    setSelectedItemId(item.id);
    onItemSelect?.(item);
  };

  return (
    <Box
      id='accountability-display'
      component='section'
      aria-labelledby='accountability-display-title'
      aria-busy={isLoading}
      data-accountability-definition-id={accountabilityDefinitionId}
      sx={{
        mt: 2.5,
        width: '100%',
        fontFamily: '"Nunito", "Helvetica", "Arial", sans-serif',
        '& .MuiTypography-root, & .MuiButtonBase-root, & .MuiTableCell-root': { fontFamily: 'inherit' },
      }}
    >
      <Box
        sx={{
          overflow: 'hidden',
          border: `1px solid ${BORDER_COLOR}`,
          borderRadius: '7px',
          backgroundColor: '#FFFFFF',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent='space-between'
          gap={1}
          sx={{ minHeight: 52, px: 2.1, py: 1.2, backgroundColor: '#FAFBFC' }}
        >
          <Typography
            id='accountability-display-title'
            component='h2'
            sx={{ m: 0, color: '#27364A', fontSize: 15, fontWeight: 800, lineHeight: 1.3 }}
          >
            {data?.data.definitionName || 'Accountability'}
          </Typography>

          <ButtonBase
            type='button'
            aria-label='Show accountability chart'
            aria-haspopup='dialog'
            onClick={() => setIsChartOpen(true)}
            sx={{
              minHeight: 32,
              gap: 0.35,
              px: 1.15,
              borderRadius: 1,
              color: LINK_COLOR,
              fontSize: 12,
              fontWeight: 800,
              '&:hover': { backgroundColor: 'rgba(63, 80, 181, 0.08)' },
              '&:focus-visible': {
                outline: '3px solid rgba(63, 80, 181, 0.2)',
                outlineOffset: 1,
              },
            }}
          >
            Shows
            <KeyboardArrowRightRoundedIcon aria-hidden='true' sx={{ fontSize: 20 }} />
          </ButtonBase>
        </Stack>

        <TableContainer id='accountability-table' sx={{ overflowX: 'auto' }}>
          <Table aria-label='Organisational units' size='small' sx={{ minWidth: TABLE_MIN_WIDTH, tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                {[data?.data.kTreeName || 'Organisational units', 'Type', 'Manager', 'Responsibility', ''].map((label, index) => (
                  <TableCell
                    key={label || 'actions'}
                    aria-hidden={!label || undefined}
                    sx={{
                      width: index === 0 ? '37%' : index === 1 ? '17%' : index === 2 ? '14%' : index === 3 ? '27%' : '5%',
                      height: 36,
                      px: index === 4 ? 1.5 : 2.1,
                      py: 0.8,
                      borderBottomColor: BORDER_COLOR,
                      color: '#8993A5',
                      backgroundColor: '#FFFFFF',
                      fontSize: 11,
                      fontWeight: 800,
                      lineHeight: 1.25,
                      textTransform: 'uppercase',
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell colSpan={5} sx={{ px: 2.1, py: 1.2, borderBottomColor: BORDER_COLOR }}>
                      <Skeleton variant='rounded' height={25} animation='wave' />
                    </TableCell>
                  </TableRow>
                ))
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ px: 2.1, py: 2, borderBottom: 0 }}>
                    <Alert
                      severity='error'
                      action={
                        <Button color='inherit' size='small' onClick={() => refetch()}>
                          Retry
                        </Button>
                      }
                    >
                      Unable to load direct accountabilities.
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ px: 2.1, py: 4, borderBottom: 0, textAlign: 'center' }}>
                    <Typography sx={{ color: SECONDARY_TEXT_COLOR, fontSize: 12, fontWeight: 700 }}>
                      No organisational units found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  <AccountabilityGroup
                    label='Managed by you (Direct Accountabilities)'
                    color='#168F70'
                    backgroundColor='#EFFAF7'
                    items={managedByCurrentUser}
                    selectedItemId={selectedItemId}
                    onSelect={handleSelect}
                  />
                  <AccountabilityGroup
                    label='Managed by colleagues (Indirect Accountabilities)'
                    color='#5867C7'
                    backgroundColor='#F4F5FE'
                    items={managedByColleagues}
                    selectedItemId={selectedItemId}
                    onSelect={handleSelect}
                  />
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Stack direction='row' alignItems='flex-start' gap={1} sx={{ mt: 1.2, px: 0.5 }} aria-live='polite'>
        
        <Typography sx={{ color: '#718096', fontSize: 12, fontWeight: 600, lineHeight: 1.55 }}>
          {isLoading
            ? 'Loading organisational units...'
            : isError
            ? 'Accountabilities could not be loaded. Retry when you are ready.'
            : selectedItem
            ? `${selectedItem.name}: ${selectedItem.responsibility}, managed by ${selectedItem.manager.toLocaleLowerCase() === 'you' ? 'you' : selectedItem.manager}.`
            : items.length === 0
            ? 'No organisational units are available for this accountability definition.'
            : 'Select any organisational unit to view more information.'}
        </Typography>
      </Stack>

      <AccountabilityChartDialog
        open={isChartOpen}
        definitionId={accountabilityDefinitionId}
        definitionName={data?.data.definitionName}
        chartScope='me'
        onClose={() => setIsChartOpen(false)}
      />
    </Box>
  );
}
